import { useSiteSettingsPublished } from './useSiteSettingsPublished';
import {
  DEFAULT_PUBLIC_SITE_PROGRESS_STEPS,
  resolveProgressSteps,
  type PublicSiteProgressStep,
} from '../lib/progressContent';
export { DEFAULT_PUBLIC_SITE_PROGRESS_STEPS } from '../lib/progressContent';

export const PUBLIC_SITE_SHELL_SETTINGS_KEY = 'public_site_shell';

export interface PublicSiteShellSettings {
  showProgressIndicator: boolean;
  progressIndicatorPageKeys: string[];
  progressIndicatorSteps: PublicSiteProgressStep[];
  showThemeToggle: boolean;
  themeTogglePageKeys: string[];
  themeTogglePosition: 'top-left' | 'top-right';
  showBackToTop: boolean;
  backToTopPageKeys: string[];
  backToTopPosition: 'bottom-left' | 'bottom-right';
  showSeasonalEffects: boolean;
  seasonalEffectsPageKeys: string[];
  showThankYouOverlay: boolean;
  thankYouTitle: string;
  thankYouMessage: string;
  thankYouButtonLabel: string;
  enquiryEmailTo: string;
  productSelectScrollTargetId: string;
  configureAnotherScrollTargetId: string;
  configureAnotherToastMessage: string;
}

export const DEFAULT_PUBLIC_SITE_SHELL_SETTINGS: PublicSiteShellSettings = {
  showProgressIndicator: true,
  progressIndicatorPageKeys: [],
  progressIndicatorSteps: DEFAULT_PUBLIC_SITE_PROGRESS_STEPS,
  showThemeToggle: true,
  themeTogglePageKeys: [],
  themeTogglePosition: 'top-right',
  showBackToTop: true,
  backToTopPageKeys: [],
  backToTopPosition: 'bottom-right',
  showSeasonalEffects: true,
  seasonalEffectsPageKeys: [],
  showThankYouOverlay: true,
  thankYouTitle: 'Thank You!',
  thankYouMessage:
    "We've received your enquiry and will get back to you shortly with more details and photos that match your colour choices.",
  thankYouButtonLabel: 'Close',
  enquiryEmailTo: 'info@eco3dcor.com',
  productSelectScrollTargetId: 'colour-selection',
  configureAnotherScrollTargetId: 'collection',
  configureAnotherToastMessage: 'Ready to configure another vase',
};

function normalizeStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return Array.from(
    new Set(
      value
        .map((entry) => (typeof entry === 'string' ? entry.trim() : ''))
        .filter(Boolean)
    )
  );
}

function normalizeString(value: unknown, fallback: string) {
  return typeof value === 'string' && value.trim().length > 0 ? value.trim() : fallback;
}

function normalizeThemeTogglePosition(value: unknown): 'top-left' | 'top-right' {
  return value === 'top-left' || value === 'top-right'
    ? value
    : DEFAULT_PUBLIC_SITE_SHELL_SETTINGS.themeTogglePosition;
}

function normalizeBackToTopPosition(value: unknown): 'bottom-left' | 'bottom-right' {
  return value === 'bottom-left' || value === 'bottom-right'
    ? value
    : DEFAULT_PUBLIC_SITE_SHELL_SETTINGS.backToTopPosition;
}

export function normalizePublicSiteShellSettings(value: unknown): PublicSiteShellSettings {
  const raw = (value && typeof value === 'object' ? value : {}) as Record<string, unknown>;

  return {
    showProgressIndicator:
      typeof raw.showProgressIndicator === 'boolean'
        ? raw.showProgressIndicator
        : DEFAULT_PUBLIC_SITE_SHELL_SETTINGS.showProgressIndicator,
    progressIndicatorPageKeys:
      normalizeStringArray(raw.progressIndicatorPageKeys),
    progressIndicatorSteps: resolveProgressSteps(raw.progressIndicatorSteps),
    showThemeToggle:
      typeof raw.showThemeToggle === 'boolean'
        ? raw.showThemeToggle
        : DEFAULT_PUBLIC_SITE_SHELL_SETTINGS.showThemeToggle,
    themeTogglePageKeys: normalizeStringArray(raw.themeTogglePageKeys),
    themeTogglePosition: normalizeThemeTogglePosition(raw.themeTogglePosition),
    showBackToTop:
      typeof raw.showBackToTop === 'boolean'
        ? raw.showBackToTop
        : DEFAULT_PUBLIC_SITE_SHELL_SETTINGS.showBackToTop,
    backToTopPageKeys: normalizeStringArray(raw.backToTopPageKeys),
    backToTopPosition: normalizeBackToTopPosition(raw.backToTopPosition),
    showSeasonalEffects:
      typeof raw.showSeasonalEffects === 'boolean'
        ? raw.showSeasonalEffects
        : DEFAULT_PUBLIC_SITE_SHELL_SETTINGS.showSeasonalEffects,
    seasonalEffectsPageKeys: normalizeStringArray(raw.seasonalEffectsPageKeys),
    showThankYouOverlay:
      typeof raw.showThankYouOverlay === 'boolean'
        ? raw.showThankYouOverlay
        : DEFAULT_PUBLIC_SITE_SHELL_SETTINGS.showThankYouOverlay,
    thankYouTitle: normalizeString(
      raw.thankYouTitle,
      DEFAULT_PUBLIC_SITE_SHELL_SETTINGS.thankYouTitle
    ),
    thankYouMessage: normalizeString(
      raw.thankYouMessage,
      DEFAULT_PUBLIC_SITE_SHELL_SETTINGS.thankYouMessage
    ),
    thankYouButtonLabel: normalizeString(
      raw.thankYouButtonLabel,
      DEFAULT_PUBLIC_SITE_SHELL_SETTINGS.thankYouButtonLabel
    ),
    enquiryEmailTo: normalizeString(
      raw.enquiryEmailTo,
      DEFAULT_PUBLIC_SITE_SHELL_SETTINGS.enquiryEmailTo
    ),
    productSelectScrollTargetId: normalizeString(
      raw.productSelectScrollTargetId,
      DEFAULT_PUBLIC_SITE_SHELL_SETTINGS.productSelectScrollTargetId
    ),
    configureAnotherScrollTargetId: normalizeString(
      raw.configureAnotherScrollTargetId,
      DEFAULT_PUBLIC_SITE_SHELL_SETTINGS.configureAnotherScrollTargetId
    ),
    configureAnotherToastMessage: normalizeString(
      raw.configureAnotherToastMessage,
      DEFAULT_PUBLIC_SITE_SHELL_SETTINGS.configureAnotherToastMessage
    ),
  };
}

export function usePublicSiteShellSettings(mode: 'published' | 'draft' = 'published') {
  return useSiteSettingsPublished(PUBLIC_SITE_SHELL_SETTINGS_KEY, {
    defaultValue: DEFAULT_PUBLIC_SITE_SHELL_SETTINGS,
    normalize: normalizePublicSiteShellSettings,
    mode,
  });
}

export function shouldRenderOnPage(pageKey: string, allowedPageKeys: string[]) {
  if (!allowedPageKeys.length) return true;
  return allowedPageKeys.includes(pageKey);
}
